// The heat pass and the clarify surface (Phase 2).
//
// One card at a time, forced choice, oldest-first (boss hotter). The heat pass
// is the lighter first pass — two taps, hot or cold; clarify is the six routes.
// Both read projections from triage.ts and commit intent batches from
// triage-intents.ts through the gate. Neither touches the log directly.
//
// Everything the user reads is set with textContent. Every control is a real
// <button> at full target size, keyboard-first, and the current item is
// announced in a live region so a screen-reader user is told what they are
// triaging. Activating a control removes it (the card advances), so focus is
// moved to the prompt heading rather than left to fall to <body> (WCAG 2.4.3).

import type { Session } from './session.ts';
import { unclarified, needsHeat } from '../triage.ts';
import {
  clocksOf, demandClocksOf, fileReceiptWords, fileUnderEvents, fileUnderNewEvents,
  datePlaceEvents, heatEvents, placeReturnDays, routeEvents, undoRouteEvents,
  restorableClocksOf, type RestorableClock,
} from './triage-intents.ts';
import { CONTAINER_KINDS, CONTAINER_ORDER, CONTAINER_DEFAULT} from '../tree.ts';
import { captureContextWords } from '../capture-context.ts';
import { allContexts, contextsOf } from '../contexts.ts';
import { attachContextEvents } from './detail-intents.ts';
import { timerMinutesOf, timerWords, timerWordsLower } from '../timer.ts';
import { doneEvents } from './work.ts';
import type { AppEvent, ClarifyRoute, Heat, MenuCategory, NodeKind } from '../events.ts';
import { MENU_CATEGORIES, MENU_WORDS } from '../menu.ts';

const ROUTES: { route: ClarifyRoute; label: string; hint: string }[] = [
  { route: 'do-now', label: 'Do now', hint: 'this one is for today — two minutes if you want them' },
  { route: 'next-action', label: 'Next action', hint: 'a real next step, comes back tomorrow' },
  { route: 'waiting-for', label: 'Waiting for', hint: 'someone else owes you this' },
  // NOT "no clock" (3.23.6). The Menu drops every demand clock and keeps the
  // gate's own capture cure, so a thing sent here does carry one — and two
  // surfaces read it out. Promising an absence the store does not have is the
  // half of that defect that lives in the copy. `replan-intents.ts` retired
  // this exact phrase for this exact reason; these are its words.
  { route: 'someday', label: 'Someday', hint: 'onto the Menu \u2014 nothing owed, no date to meet' },
  { route: 'reference', label: 'Reference', hint: 'keep it, don’t act on it' },
  { route: 'trash', label: 'Trash', hint: 'not a thing after all' },
];

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
};

export interface TriageUI {
  /** `after: 'capture'` refreshes the contents WITHOUT letting the surface
   *  reveal itself — see `suppressed`. */
  refresh(after?: 'capture' | 'ask'): void;
  /** Re-word an on-screen do-now offer after the timer length changes (1.10.0).
   *  Separate from `refresh` on purpose — see the note at its definition. */
  relabelTimer(): void;
}

/** Mount the triage surface. `onChange` lets the shell re-render its own list
 *  (the held-items view) when triage moves an item. `openDetail` opens the
 *  card's detail sheet — what makes rename, a real date, filing and a person
 *  reachable mid-triage (1.3.0) without the six routes growing a seventh. */
export function mountTriage(
  session: Session, onChange: () => void,
  openDetail?: (n: import('../fold.ts').NodeState) => void,
): TriageUI {
  const region = document.querySelector<HTMLElement>('#triage');
  const card = document.querySelector<HTMLElement>('#triage-card');
  const prompt = document.querySelector<HTMLElement>('#triage-prompt');
  const actions = document.querySelector<HTMLElement>('#triage-actions');
  const gauge = document.querySelector<HTMLElement>('#triage-gauge');
  const live = document.querySelector<HTMLElement>('#triage-live');
  const donow = document.querySelector<HTMLElement>('#triage-donow');
  if (!region || !card || !prompt || !actions || !gauge || !live || !donow) return { refresh() {}, relabelTimer() {} };
  // Non-null bindings, so the nested refresh() closure keeps the narrowing the
  // guard above established.
  const REGION = region, CARD = card, PROMPT = prompt, ACTIONS = actions, GAUGE = gauge, LIVE = live, DONOW = donow;
  /**
   * HOW MANY ARE HERE (3.10.0). Optional like `#triage-count`, and for the same
   * reason: markup presence is a gate's job, not a runtime crash. The smoke walk
   * asserts it.
   *
   * GUARDED WRITE, ALWAYS. `watchJobs` in `hub.ts` observes `hidden` on any
   * element under `main`, subtree included, and repaints for each change — so an
   * unguarded write here feeds that observer on every repaint and the page never
   * settles. That cost 3.9.1 a walk and it presents as a timeout somewhere
   * unrelated (hub LESSONS 181).
   */
  const hereLine = document.querySelector<HTMLElement>('#triage-here');
  const setHere = (words: string | null): void => {
    if (!hereLine) return;
    if (words === null) {
      if (!hereLine.hidden) hereLine.hidden = true;
      return;
    }
    if (hereLine.textContent !== words) hereLine.textContent = words;
    if (hereLine.hidden) hereLine.hidden = false;
  };
  const captureInput = (): HTMLElement | null => document.querySelector<HTMLElement>('#capture');

  // The last-action undo lives OUTSIDE the triage section, beside the do-now
  // offer — for the same reason that does: routing your last inbox item hides
  // the whole section, and the way to take that route back must not vanish with
  // it. Optional, so older markup without it simply has no undo.
  const undoRegion = document.querySelector<HTMLElement>('#triage-undo');
  const openBtn = document.querySelector<HTMLButtonElement>('#triage-open');

  /** Whether this surface may put itself on screen (1.39.2; inverted 1.42.1).
   *
   *  TRUE UNTIL ASKED. Sorting is a place you go, never a place you are sent.
   *
   *  1.39.2 stopped a capture from turning this on, which fixed the ten-captures-
   *  ten-interruptions path. It left the other door open: arriving with anything
   *  unsorted put a forced choice on screen before the one thing you could do —
   *  `#triage` is markup order 218 and `#nextup` is 384, so the eight-way
   *  decision was literally above the answer to "what now".
   *
   *  The old value was defended on the grounds that a fresh arrival is a fresh
   *  decision, so coming back should show what waited. That reasoning assumed
   *  the reveal was protecting the items. It is not: `cureFor` gives every
   *  `capture.recorded` a same-day clock IN THE SAME TRANSACTION (gate.ts), so
   *  an unsorted capture is covered the instant it is written, `whyCovered`
   *  returns 'clock' for it, and it is counted in the proof like everything
   *  else. Nothing is at risk of going quiet while it sits here.
   *
   *  So what the reveal actually did was charge a decision for arriving. That is
   *  the corridor ADR-0084 named and ADR-0085 removes. The door below is how the
   *  inbox is reached, and it is always there while anything waits — reachable,
   *  never in front. */
  let suppressed = true;

  /** Whether a hot/cold sweep is under way (1.39.3).
   *
   *  The threshold decides whether to BEGIN one, never whether to carry on. A
   *  sweep that abandoned you at item four of six — because the queue had
   *  dropped under the bar it started above — would be worse than never offering
   *  one: you would be halfway through a rhythm and dropped into a different
   *  question with no warning. Latched on when it starts, off when the queue is
   *  empty, and nowhere else. */
  let sweepUnderWay = false;

  /**
   * WHEN IT WAS WRITTEN (1.23.0) — the one thing triage has never been able to
   * tell you about a card.
   *
   * `docs/nd-collisions.md` entry 17: the context that made a fragment
   * meaningful lives in working memory and is gone within hours, so "call about
   * the thing" arrives here as a stranger's note and gets routed blind, or
   * trashed, or kept out of a vague sense it might have mattered. Capture is
   * right to ask nothing at write time; the log already knows the answer and
   * nobody has ever been shown it.
   *
   * IT NEVER BLOCKS, and that is the whole shape of this code. The card renders
   * from state, synchronously, exactly as it always has; this fills in
   * afterwards or not at all. Nothing on the path to a first capture waits on a
   * store read (ADR-0001), and a store that is slow or broken costs a line of
   * gray text rather than the item somebody was deciding about.
   *
   * The `showing` guard is the other half: a lookup resolving after the card
   * has moved on would attach one item's history to another item's title, which
   * is worse than saying nothing at all.
   */
  const contextLine = document.querySelector<HTMLElement>('#triage-where');
  /** Which node the context line currently describes, so a repaint for the SAME
   *  card does not blank it. */
  let contextFor: string | null = null;

  const paintContext = (nodeId: string | null): void => {
    if (!contextLine) return;
    // CLEARED ONLY WHEN THE CARD CHANGES (1.35.0).
    //
    // It used to clear on every render, which is right about the danger — the
    // previous card's line must never survive onto a new one — and wrong about
    // the frequency. `refresh` runs after every commit, and the refill is an
    // async read from the log, so any repaint blanked a correct line and left it
    // blank until that read returned. For a reader it is a line that vanishes
    // and comes back; for the a11y gate it was a registry entry matching nothing
    // visible, reproducibly, which is how it was found.
    //
    // Keyed on the node, so the guarantee that mattered is unchanged: a
    // different card always clears first.
    if (nodeId !== contextFor) {
      contextLine.textContent = '';
      contextLine.hidden = true;
      contextFor = nodeId;
    }
    if (!nodeId) return;
    void session.store.firstEventFor(nodeId)
      .then((first) => {
        if (!first || showing !== nodeId || !contextLine) return;
        const words = captureContextWords(first.at, session.zone, new Date().toISOString());
        if (!words) return;
        contextLine.textContent = words;
        contextLine.hidden = false;
      })
      .catch(() => { /* a line of context, never the card */ });
  };

  // The one running do-now timer, if any. It lives in DONOW (a stable region
  // outside the card carousel) so refresh() advancing the card never touches it.
  // The one running timer, if any. `stop` takes no verdict — closing a timer
  // records its span and nothing about why it closed (1.10.0, ADR-0059).
  let active: { stop: (andDone?: boolean) => void } | null = null;

  /**
   * PASSED OVER THIS SESSION (1.25.0) — a way out of a surface that had none.
   *
   * Reported from a phone: paths in without a path out. The heat pass offered
   * Hot and Cold, clarify offered seven routes, and neither had a skip — while
   * Next up has had "Not this" since ADR-0030. `unclarified` is oldest-first
   * and stable, so a card somebody could not decide about was not merely
   * awkward: it was the SAME card at the top of the surface every time the app
   * opened, for ever. The wall this app exists to prevent, built into the
   * surface whose job is to drain the inbox.
   *
   * IN MEMORY, AND NOWHERE ELSE — Next up's rule exactly. A skip that survived
   * a reload would be a record of a decision the app promised not to keep, and
   * on THIS surface it would be worse than on the offer: a durable list of what
   * somebody could not face is the wall rebuilt one layer down, with the app
   * keeping it for them. Nothing is written, nothing is counted, and the gauge
   * never mentions it.
   */
  const passed = new Set<string>();

  /**
   * SORT IT WITHOUT THE HEAT PASS (V2 stage 3).
   *
   * ADR-0029 says heat is "optional-first on purpose" and that forcing two
   * passes on every item would be a cost with no return. It was not optional in
   * fact: the surface renders the heat card whenever anything is unheated, so
   * the only ways past it were to answer Hot/Cold — which records a heat
   * somebody did not mean — or to pass the card over, which moves to the NEXT
   * item rather than letting you sort THIS one.
   *
   * `unclarified` never filtered on heat, so the item was in the clarify queue
   * the whole time. The gate was purely in what the surface chose to show.
   *
   * IN MEMORY, like `passed`, and for the same reason: it is a preference about
   * this sitting, not a fact about the item. Nothing is written and no heat is
   * recorded — an item sorted this way simply has none, which is exactly what
   * "optional" means.
   */
  const straightToSort = new Set<string>();

  /** Which node the card currently shows, so tapping it can open the right
   *  sheet. The card is a real <button> since 1.3.0. */
  let showing: string | null = null;
  CARD.addEventListener('click', () => {
    if (!showing || !openDetail) return;
    const n = session.state().nodes.get(showing);
    if (n) openDetail(n);
  });

  /** Commit a batch; report success so callers only chain success-only effects
   *  (like starting the do-now timer). Announces the outcome in the live region;
   *  on a gate rejection the same card is re-rendered and the message survives. */
  const commit = async (make: Parameters<Session['commit']>[0], announce: string): Promise<boolean> => {
    let ok = true;
    try {
      await session.commit(make);
      LIVE.textContent = announce;
    } catch (err) {
      ok = false;
      LIVE.textContent = `Couldn’t do that — ${(err as Error).message}`;
    }
    onChange();
    refresh();
    return ok;
  };

  /** After an action removes the control the user activated, put focus somewhere
   *  real: the prompt of the next card, or the capture line once the inbox is
   *  clear. Focusing a non-actionable heading (not the first route) avoids an
   *  accidental double-activation of a destructive route like Trash. */
  const restoreFocus = (): void => {
    if (!REGION.hidden) PROMPT.focus();
    else captureInput()?.focus();
  };

  /** Record that the item is finished. The ONE thing the do-now flow could not
   *  do, and the thing it most obviously needed: routing something to "Do now"
   *  clocked it for today and then offered no way to say you had done it, so a
   *  two-minute job sat under "Ready now" until it was found in the list
   *  (found on device). Gated like every other completion. */
  const markDone = (node: string): void => {
    void session.commit(ctx => doneEvents(ctx, node)).then(() => {
      LIVE.textContent = 'Done.';
      onChange();
    }).catch((err: Error) => { LIVE.textContent = `Couldn’t record that — ${err.message}`; });
  };

  /**
   * What a just-routed "Do now" offers: finish it, or start two minutes.
   *
   * **The timer is an offering, not a gate.** It used to start on its own the
   * moment the route landed, which made a category ("this one is for now") into
   * a stopwatch nobody asked for. The category is the useful part; the two
   * minutes are a tool some people want and others do not.
   */
  const offerDoNow = (node: string): void => {
    active?.stop();
    // NAME the item. The offer used to say "Now — finish it, or take two minutes"
    // with no hint of WHAT, so a fast router landed on a bar demanding an answer
    // about a thing it would not name (found on device).
    const title = session.state().nodes.get(node)?.title || '(untitled)';
    const bar = el('div', 'donow');
    bar.append(el('span', 'donow-label', `Now: ${title}`));
    const done = el('button', 'donow-done', 'Done');
    done.type = 'button';
    // Distinct from Next up's Done, which sits on the same screen (§4). Leads
    // with the visible word so saying it still works (SC 2.5.3).
    done.setAttribute('aria-label', 'Done with what you are on now');
    done.addEventListener('click', () => { DONOW.replaceChildren(); markDone(node); });
    const start = el('button', 'ghost', `Start ${timerWordsLower(timerMinutesOf(session.state()))}`);
    start.dataset.startTimer = 'yes';
    start.type = 'button';
    start.addEventListener('click', () => startDoNowTimer(node));
    // A WAY OUT that keeps it for today. The offer is an offering, not a gate —
    // but "Done" and a timer were the only exits, so a category ("this one is for
    // today") became a trap with no way to simply agree and move on. Leaving it
    // dismisses the offer; the item stays clocked for today, waiting under Next up.
    const leave = el('button', 'ghost', 'Leave it for now');
    leave.type = 'button';
    leave.addEventListener('click', () => {
      DONOW.replaceChildren();
      LIVE.textContent = 'Left for today — it is waiting under Next up.';
      restoreFocus();
    });
    bar.append(done, start, leave);
    DONOW.replaceChildren(bar);
  };

  /**
   * The timer, started only when asked for — PRESENCE, NOT PROGRESS
   * (1.10.0, ADR-0059).
   *
   * It shows that it is running and says what you chose, and it shows nothing
   * about how far through you are. See `src/timer.ts` for why: any rendering of
   * progress toward a chosen end is a fraction, and a fraction is a score.
   *
   * Stopping writes NOTHING about stopping. `src/requests.ts` and ADR-0056 say
   * the do-now offer's "Not now" is "event-free, forever", and until 1.10.0 the
   * timer beside it wrote `outcome: 'abandoned'` on every stop — the same flow
   * keeping a record the same flow forbids.
   */
  const startDoNowTimer = (node: string): void => {
    // Starting a new timer while one runs closes the old one. Its SPAN is
    // recorded, because a span is a fact about what you did; nothing about why
    // it ended is, because that would be a verdict.
    active?.stop();

    const startedAt = new Date().toISOString();
    const minutes = timerMinutesOf(session.state());
    // `data-seconds` on the timer's own region stays a deliberate test seam —
    // a gate cannot wait twenty real minutes to check what happens at the end.
    // Nothing in the app writes it, so shipped behavior is always the choice.
    const DURATION = Number(DONOW.dataset.seconds) || minutes * 60;
    let ended = false;
    let timeout: number | undefined;

    const bar = el('div', 'donow');
    // The commitment, in words. Not a countdown, not a fill: a sentence, which
    // is the one form that can hold something you may walk away from.
    const label = el('span', 'donow-label', `${timerWords(minutes)}, running.`);
    // The presence mark. It says "on" and nothing else — no fill, no fraction,
    // no growth. CSS gives it a gentle pulse and a static fallback under
    // prefers-reduced-motion; either way it never encodes an amount.
    const mark = el('span', 'donow-running');
    mark.setAttribute('aria-hidden', 'true');
    // Done is available THROUGHOUT, in one tap. Finishing in forty seconds is
    // the good case, and it should not require stopping a timer first.
    const doneBtn = el('button', 'donow-done', 'Done');
    doneBtn.type = 'button';
    doneBtn.setAttribute('aria-label', 'Done with what you are on now');
    // "Stop" and not "Give up". Leaving is an ordinary move with no cost.
    const stopBtn = el('button', 'ghost', 'Stop');
    stopBtn.type = 'button';
    bar.append(mark, label, doneBtn, stopBtn);
    DONOW.replaceChildren(bar);

    /**
     * Close the timer. ONE `do-now.timed` carries the span and nothing else —
     * the `focus.started` / `focus.ended` shape, which records what you did
     * without judging it. The chosen length is deliberately absent from the
     * payload, so a shortfall cannot be reconstructed by subtraction (the
     * arithmetic that got the report's "Started" section deleted in 1.9.0).
     */
    const finish = (andDone = false): void => {
      if (ended) return;
      ended = true;
      if (timeout !== undefined) clearTimeout(timeout);
      if (active === handle) active = null;
      bar.remove();
      void session.commit(ctx => [{
        id: ctx.id(), vault: ctx.vault, at: ctx.at, device: ctx.device, seq: ctx.seq(),
        kind: 'do-now.timed', node,
        payload: { startedAt, endedAt: new Date().toISOString() },
      } as unknown as AppEvent]).catch(() => {});
      if (andDone) markDone(node);
    };
    const handle = { stop: finish };
    active = handle;

    /**
     * The chosen time has passed. **The timer goes away.**
     *
     * It does not ask whether you finished. It used to record `completed` the
     * instant the clock hit zero, which was the app asserting something it had
     * never asked; then it asked, which made the chosen length the size of the
     * job. It is neither: the length was the entry price, and reaching it is
     * not an achievement to confirm or a deadline to answer for.
     *
     * The bar removes itself and one line goes to the live region. Silent
     * removal would be an accessibility defect — a control vanishing with no
     * announcement is a control that disappeared for a screen-reader user with
     * no way to know it had. The item stays clocked for today either way.
     */
    const done = (): void => {
      if (ended) return;
      LIVE.textContent = `That is ${timerWordsLower(minutes)}. It is still there when you want it.`;
      finish();
      restoreFocus();
    };

    timeout = setTimeout(done, DURATION * 1000) as unknown as number;
    doneBtn.addEventListener('click', () => finish(true));
    stopBtn.addEventListener('click', () => { finish(); restoreFocus(); });
  };

  /**
   * Drop the last-action undo. Any new triage action makes it stale — undo
   * reverses the MOST RECENT route, never an older one.
   *
   * BUT NOT THE PLACE-DATING OFFER (V2 stage 3). That control is about a PLACE,
   * not about the route just taken, and it is the only path in the app to the
   * clock that stops a place returning hollow. Clearing it with the undo meant
   * triaging the very next card destroyed the offer — and the receipt goes on
   * saying "no return date yet" for ever, with nothing left to press.
   *
   * The undo genuinely does go stale. The question "when should Errands come
   * back to you?" does not go stale one card later; it is still the same
   * unanswered question about the same place. So the bar is emptied of
   * everything EXCEPT a pending place offer, which stays until it is answered.
   */
  const clearUndo = (): void => {
    if (!undoRegion) return;
    const keep = undoRegion.querySelector('.triage-place-pending');
    if (!keep) { undoRegion.replaceChildren(); return; }
    // The kept bar loses its UNDO. That undo is about the route just superseded
    // and is exactly what has gone stale — keeping it would put two "Undo"
    // buttons on one surface, which is both a coin toss for anyone driving by
    // voice (§4: no two controls answer to one name) and an offer to take back
    // something that is no longer the last thing you did. The walk caught it as
    // two where one was expected.
    //
    // What survives is the receipt sentence and the unanswered question about
    // the place, which is the whole point.
    keep.querySelector('.triage-undo-btn')?.remove();
    undoRegion.replaceChildren(keep);
  };

  // AN UNDO IS FOR THE ROOM IT WAS MADE IN — SO IT HIDES, IT DOES NOT GO (3.9.1).
  //
  // `#triage-undo` and `#triage-donow` carry `data-always` so that routing your
  // LAST item cannot take them with it: the section hides itself the moment the
  // inbox empties, and a timer running behind a hidden section is the defect
  // they were moved out for. But `data-always` also carries them into every
  // OTHER job, and walked as a reader "Sent to Someday. Undo" was sitting under
  // the offer card, and under search, several acts later — beside a do-now bar
  // naming the same item the offer card was already showing, each with its own
  // Done.
  //
  // FIRST ATTEMPT CLEARED THEM AND WAS WRONG TWICE, and both walks said so.
  // Clearing on "not triage any more" fired when the inbox emptied and the
  // stance fell away on its own — the exact case these live outside `#triage`
  // to survive (smoke: "the Do now offer survives the triage surface hiding
  // itself"). And clearing at all threw away an undo somebody was entitled to
  // come back for: the a11y walk steps out to measure a card and returns to
  // press it.
  //
  // So: HIDDEN while you are in another named job, and back when you return.
  // A null stance is the hub — where you land when a job ends under you as much
  // as when you step back — so it does not count as having moved on. A RUNNING
  // timer is never hidden: it is live, and this bar carries the only way to
  // stop it.
  if (typeof MutationObserver !== 'undefined') {
    const runway = document.querySelector('#runway');
    if (runway) {
      // ONLY WRITE WHEN IT WOULD CHANGE — this is not a micro-optimisation, it
      // is the difference between working and hanging. `watchJobs` in `hub.ts`
      // observes `hidden` on ANY element under `main`, subtree included, and
      // repaints the hub for each one. An observer that writes `hidden` under
      // `main` therefore feeds that observer on every stance change, and the
      // page never settles long enough to be clicked: the smoke walk stopped
      // dead on `#held-fold-summary` and looked like a hang rather than a loop.
      const set = (el: HTMLElement | null, v: boolean): void => {
        if (el && el.hidden !== v) el.hidden = v;
      };
      const syncStrays = (): void => {
        const now = runway.getAttribute('data-stance');
        const away = now !== null && now !== 'triage';
        set(undoRegion, away);
        set(DONOW, away && !DONOW.querySelector('.donow-running'));
      };
      new MutationObserver(syncStrays)
        .observe(runway, { attributes: true, attributeFilter: ['data-stance'] });
      syncStrays();
    }
  }

  /**
   * Offer to take the just-made route back. Names where the card went and, in
   * one tap, returns it to the inbox — the direct answer to "it moved and I do
   * not know how to get it back". The node id, route and prior kind are captured
   * here, so undo reverses THAT card even after the surface has advanced to the
   * next one.
   */
  const showUndo = (
    node: string, route: ClarifyRoute, fromKind: NodeKind, where: string,
    words?: string,
    /** The place this went into, when it has no return date yet (V2 stage 3). */
    undatedPlace?: string | null,
    /** What the route SHED, snapshotted before it committed, so Undo can put it
     *  back (V2 stage 3). By the time this control is pressed the dates are gone
     *  from state, so they have to be carried here or they are unrecoverable. */
    shed: readonly RestorableClock[] = [],
  ): void => {
    if (!undoRegion) return;
    const bar = el('p', 'triage-undo-bar');
    const said = el('span', 'triage-undo-where', words ?? `Sent to ${where}.`);
    bar.append(said);

    /**
     * WHEN SHOULD IT COME BACK — offered where the receipt says it will not.
     *
     * V2 stage 3, and it closes the hollow return. The machinery has always
     * worked: give a place a human review clock and `heldGroups` moves it from
     * Later to Coming up to Ready now, carrying everything filed into it.
     * Nothing wrote that clock, and the only path to writing one was to know
     * the place existed, open the tree, find it, open its sheet and set a date
     * that would have been a `due` — a demand on a thing that is never done.
     *
     * So the control goes exactly where the finding is stated. The receipt has
     * said "no return date yet" since 1.20.0, which was the honest sentence and
     * an unanswerable one: information for the one person who could fix it,
     * with nothing to press.
     *
     * OFFERED, NEVER DEMANDED. It appears only on the no-date branch, it can be
     * ignored, and ignoring it changes nothing — the receipt already said what
     * is true. Filing without dating stays a complete act (law 6), and this is
     * a door rather than a question that must be answered before moving on.
     */
    if (undatedPlace) {
      // Marked so `clearUndo` can tell this apart from the undo itself: the
      // route goes stale on the next card, the unanswered question about a place
      // does not.
      bar.classList.add('triage-place-pending');
      const when = document.createElement('input');
      when.type = 'date';
      when.id = 'triage-place-when';
      when.className = 'triage-place-when';
      const wLabel = el('label', 'visually-hidden', `When should ${where} come back to you?`);
      wLabel.setAttribute('for', 'triage-place-when');
      const set = el('button', 'linklike triage-place-set', 'Bring it back on…');
      set.type = 'button';
      // THE VISIBLE WORDS LEAD THE SPOKEN NAME (SC 2.5.3). The first version
      // said "Set when ⟨place⟩ comes back to you" while the button read "Bring
      // it back on…" — a voice-control user saying what they can see would have
      // hit nothing, and the a11y gate caught it on its first run. The place's
      // name still rides along, because §4 needs each receipt's control to
      // answer to something of its own.
      set.setAttribute('aria-label', `Bring it back on — set when ${where} comes back to you`);
      set.addEventListener('click', () => {
        const key = when.value;
        // A date input yields '' when empty or invalid; nothing is a legal
        // answer, and saying so beats committing silence.
        if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) { LIVE.textContent = 'Pick a day first.'; when.focus(); return; }
        set.disabled = true;
        void session.commit(ctx => datePlaceEvents(ctx, undatedPlace, key))
          .then(() => {
            const days = placeReturnDays(
              session.state().nodes.get(undatedPlace), new Date().toISOString(), session.zone);
            // The SAME sentence the receipt speaks, from the same function, so
            // the confirmation and the receipt can never describe one date two
            // ways.
            LIVE.textContent = fileReceiptWords(where, days);
            // The sentence is REPLACED IN PLACE and only the answered question
            // is withdrawn. The first version rebuilt the whole bar, which took
            // Undo down with it — so answering "when does this come back"
            // silently cost the way to take the filing back, on the one surface
            // whose entire job is that you can. Caught by the walk timing out
            // on a control that had been correct a line earlier.
            said.textContent = fileReceiptWords(where, days);
            wLabel.remove();
            when.remove();
            set.remove();
            // Answered, so it is an ordinary receipt again and the next triage
            // action may clear it like any other.
            bar.classList.remove('triage-place-pending');
            onChange();
          })
          .catch((err: Error) => {
            set.disabled = false;
            LIVE.textContent = `That date did not save — ${err.message}`;
          });
      });
      bar.append(wLabel, when, set);
    }

    const btn = el('button', 'linklike triage-undo-btn', 'Undo');
    btn.type = 'button';
    btn.addEventListener('click', () => {
      btn.disabled = true;
      void session.commit(ctx => undoRouteEvents(ctx, node, route, fromKind, shed))
        .then(() => {
          clearUndo();
          LIVE.textContent = 'Back in your inbox.';
          onChange();
          refresh();
          restoreFocus();
        })
        .catch((err: Error) => {
          btn.disabled = false;
          LIVE.textContent = `Couldn’t undo — ${err.message}`;
        });
    });
    bar.append(btn);
    // KEEP AN UNANSWERED PLACE QUESTION ABOVE THE NEW RECEIPT.
    //
    // `clearUndo` preserving it was not enough on its own, and the walk caught
    // that before it shipped: the next route calls `clearUndo` and then this,
    // and a bare `replaceChildren(bar)` wiped the very thing `clearUndo` had
    // just spared. Both halves are needed, which is why the assertion is about
    // the surface after the next card rather than about either function.
    const pending = undoRegion.querySelector('.triage-place-pending');
    if (pending && pending !== bar) undoRegion.replaceChildren(pending, bar);
    else undoRegion.replaceChildren(bar);
  };

  /**
   * "Not this one" — the way past a card, on both passes.
   *
   * It commits nothing and announces the move rather than the decision: there
   * was no decision. The words matter as much as the behavior here, because
   * this control exists for the moment somebody cannot answer the question, and
   * a label that implied they had answered it would be the surface putting a
   * verdict in their mouth.
   *
   * Marked `ghost` like Next up's "Not this", so it reads as the way past
   * rather than as an eighth route.
   */
  const skipControl = (nodeId: string): HTMLButtonElement => {
    const b = el('button', 'route ghost');
    b.type = 'button';
    b.append(el('span', 'route-label', 'Not this one'),
             el('span', 'route-hint', 'come back to it — nothing is recorded'));
    b.addEventListener('click', () => {
      passed.add(nodeId);
      // A pending route-undo belongs to a DIFFERENT card, and the surface is
      // about to show one. Same reasoning as the heat tap above it.
      clearUndo();
      LIVE.textContent = 'Passed over. It is still in the inbox.';
      refresh();
      restoreFocus();
    });
    return b;
  };

  /**
   * The way to make the heat pass optional in fact, and it is on the heat card
   * because that is where the person is standing when they want it.
   *
   * `ghost`, like "Not this one", so it reads as a way past rather than as a
   * third heat. The hint says what it costs, which is nothing: heat is a hint
   * clarify can lean on, and an item without one is sorted the same way with one
   * fewer thing on the screen.
   */
  const sortNowControl = (nodeId: string): HTMLButtonElement => {
    const b = el('button', 'route ghost');
    b.type = 'button';
    // IT SAYS WHERE IT GOES, NOT THAT IT SKIPS (3.23.13). This read *Just sort
    // it · skip this question · nothing is recorded* and it does not skip
    // anything: it goes STRAIGHT TO the other question, which has nine answers
    // rather than two. A cold reader reached for it as the low-effort way out —
    // its own words said skip — and got the bigger grid. The button beside it,
    // *Not this one*, carries the same "nothing is recorded" reassurance and IS
    // the way past, so the two were indistinguishable at exactly the moment one
    // of them mattered. A control may not describe itself by what it declines
    // to do when what it actually does is go somewhere.
    b.append(el('span', 'route-label', 'Choose where it goes'),
             el('span', 'route-hint', 'to the sorting question — no heat recorded'));
    b.addEventListener('click', () => {
      straightToSort.add(nodeId);
      clearUndo();
      LIVE.textContent = 'Sorting it now.';
      refresh();
      restoreFocus();
    });
    return b;
  };

  const renderHeat = (nodeId: string, text: string): void => {
    PROMPT.textContent = 'Hot or cold?';
    // WHICH PASS THIS IS, as a fact rather than as wording (3.9.1). Both browser
    // walks watched the prompt's TEXT to know when the heat pass had drained —
    // `startsWith('Clarify')` — so renaming the prompt in this release timed them
    // out. Which step you are on is structural; what it says is copy.
    PROMPT.dataset.step = 'heat';
    showing = nodeId;
    CARD.textContent = text;
    paintContext(nodeId);
    ACTIONS.replaceChildren(...(['hot', 'cold'] as Heat[]).map(h => {
      const b = el('button', 'route');
      b.type = 'button';
      // THE LINE THAT ANSWERS "WHEN DO THE COLD ONES HIDE FROM ME?" (3.8.1).
      //
      // Never. Cold sorts last, still counts, still comes back, and still fills
      // the offer when it is all there is — `HEAT_ORDER` in `src/nextup.ts` and
      // the assertions in `test/heat-ranking.test.ts` have said so since 2.7.0,
      // and nothing on any screen ever did. Reported from a device: not knowing
      // is what turns a two-tap triage into something you stop trusting, and an
      // app you do not trust to be holding everything is one you leave.
      //
      // On the CONTROL rather than as a standing paragraph, because that is
      // this surface's own shape — every one of the six routes below carries a
      // label and a hint — and because the doubt arrives at the moment of
      // pressing, not before it.
      b.append(el('span', 'route-label', h === 'hot' ? 'Hot' : 'Cold'));
      // BOTH OF THEM SAY WHAT THEY ARE (3.23.13). Cold got its line because the
      // doubt it answers was reported from a device — "when do the cold ones
      // hide from me?" — and Hot was left bare because nobody had asked
      // anything about it. A cold reader then stopped ON THIS SCREEN, ninety
      // seconds in, because Hot was the only control in the flow with no
      // explanation and it could not tell what it was being asked. A control
      // with no hint beside controls that have them does not read as simple; it
      // reads as the one you are supposed to already understand.
      b.append(el('span', 'route-hint', h === 'hot'
        ? 'offered before the cold ones — that is all it does'
        : 'still comes back — nothing is ever hidden'));
      b.addEventListener('click', () => {
        // A heat pass is a new action, so any pending route-undo is now stale.
        clearUndo();
        void commit(ctx => heatEvents(ctx, nodeId, h), `Marked ${h}.`).then(restoreFocus);
      });
      return b;
    }));
    ACTIONS.append(sortNowControl(nodeId), skipControl(nodeId));
  };

  /**
   * WHERE, which triage has never been able to answer.
   *
   * Reported 2026-08-04: a backlog imported to work through and file in the
   * right places, and the places kept turning out not to exist yet. That was
   * the problem. The six routes above all answer WHEN. This one answers where,
   * and it MAKES the place when it is not there — law 4, levels push down and
   * the user never climbs. Sending somebody off to create a project and come back is
   * the climb, and across a 1,173-item import it is the whole difficulty.
   *
   * The picker replaces the routes rather than opening over them: one decision
   * is on screen at a time, and Back is the first control wired (§14).
   */
  const renderPlaces = (nodeId: string, text: string, kind: string, heat: Heat | null): void => {
    const st = session.state();
    const places = [...st.nodes.values()]
      .filter(n => !n.trashed && !n.mergedInto && CONTAINER_KINDS.has(n.kind) && n.id !== nodeId)
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''));

    // THE DETAIL SHEET'S OWN QUESTION (3.11.1). It read "Where does it go?",
    // which is the place question and this is the container one. `#detail-parent`
    // has asked "What is this part of?" all along, and two surfaces asking one
    // question in two words is how the vocabulary came apart in the first place.
    PROMPT.textContent = 'What is this part of?';
    CARD.textContent = text;

    const back = el('button', 'route ghost');
    back.type = 'button';
    back.append(el('span', 'route-label', 'Back'), el('span', 'route-hint', 'keep deciding when instead'));
    back.addEventListener('click', () => renderClarify(nodeId, text, kind, heat));

    // The receipt (V2 stage 1). Computable BEFORE the commit in both paths: an
    // existing place's clocks are already in state, and a place minted by the
    // file itself has, by construction, no human clock yet — so its receipt is
    // always the honest no-date branch. That branch is the hollow-return
    // finding surfaced to the one person who can date the place.
    const fileInto = (
      make: (c: Parameters<Session['commit']>[0] extends (ctx: infer C) => unknown ? C : never) => AppEvent[],
      where: string,
      place: import('../fold.ts').NodeState | null,
    ): void => {
      clearUndo();
      const days = placeReturnDays(place, new Date().toISOString(), session.zone);
      const receipt = fileReceiptWords(where, days);
      void commit(make as Parameters<Session['commit']>[0], receipt).then(ok => {
        // WHICH place, read back from state AFTER the write. A place minted by
        // this very act has its id chosen inside `createParentEvents`, and the
        // honest way to learn it is to ask what the item is now under rather
        // than to thread an id back out of an event builder. It also covers the
        // existing-place branch with the same line.
        const landedIn = ok ? (session.state().nodes.get(nodeId)?.parent ?? null) : null;
        // Offered only where the receipt says there is no date. A place that
        // already comes round needs nothing, and a control that appeared anyway
        // would be asking a question the sentence beside it has just answered.
        if (ok) showUndo(nodeId, 'filed', kind as NodeKind, where, receipt,
          days === null ? landedIn : null);
        restoreFocus();
      });
    };

    const rows: HTMLElement[] = [back];

    // Naming a place that does not exist is the FIRST thing offered, because it
    // is the case that was missing and the one an import runs into constantly.
    const form = el('div', 'place-new');
    const input = document.createElement('input');
    input.type = 'text';
    input.id = 'triage-place-new';
    // STILL SAID "PLACE" ONE SCREEN DEEPER (3.13.0). 3.11.1 renamed the route
    // that opens this screen, because it files into a CONTAINER and a *place* is
    // where work can be done — a different thing the app names elsewhere. The
    // sub-screen kept the old word, so the collision survived the fix, and it
    // became live again the moment a real context route landed beside it.
    input.placeholder = 'Name a new project, area or goal';
    const label = el('label', 'visually-hidden', 'Name a new project, area or goal to put this in');
    label.setAttribute('for', 'triage-place-new');
    // WHICH OF THE THREE THIS SCREEN HAS BEEN NAMING (3.24.1).
    //
    // The field above says "Name a new project, area or goal", its label says
    // it again, the empty-field message says it a third time and the route hint
    // that opens this screen says it a fourth — and until now whatever you
    // typed became a project. Four promises and no way to answer one. A cold
    // read made four places this way, one meant as an ongoing area, and every
    // one came back a project.
    //
    // HERE RATHER THAN ANYWHERE ELSE, and that is `CONTAINER_ORDER`'s own
    // ruling rather than a fresh judgment: the list "belongs where somebody is
    // already typing a name, so choosing what kind of thing it is happens in
    // the same breath as saying what it is called". This is that moment.
    // `project` leads and stays selected, so the ordinary path costs no extra
    // thought and nobody has to decide anything they were not already deciding
    // implicitly.
    //
    // FILLED FROM `CONTAINER_ORDER`, never from a list written here — the same
    // rule the two selects on the detail sheet follow, for the same reason.
    const kindSel = document.createElement('select');
    kindSel.id = 'triage-place-kind';
    kindSel.setAttribute('aria-label', 'What kind of place to make');
    for (const [kind, words] of CONTAINER_ORDER) {
      const o = document.createElement('option');
      o.value = kind;
      o.textContent = words;
      kindSel.append(o);
    }
    kindSel.value = CONTAINER_DEFAULT;
    const make = el('button', 'route');
    make.type = 'button';
    make.append(el('span', 'route-label', 'Make it'), el('span', 'route-hint', 'a new one, and put this in it'));
    make.addEventListener('click', () => {
      const title = input.value.trim();
      // IT SAYS WHY NOTHING HAPPENED (3.23.10). This focused the field and
      // returned, so pressing *Make it* with nothing typed was a no-op that
      // explained itself only to somebody watching the caret. The date controls
      // on a thing's own page already answer this case in words ("Pick a date
      // first."); this is the same case on this surface.
      if (!title) {
        LIVE.textContent = 'Type a name for the new project, area or goal first.';
        input.focus();
        return;
      }
      const chosen = kindSel.value as NodeKind;
      fileInto(
        c => fileUnderNewEvents(
          c, nodeId, title, clocksOf(session.state().nodes.get(nodeId)), null, chosen),
        title, null);
    });
    form.append(label, input, kindSel, make);
    rows.push(form);

    for (const p of places) {
      const b = el('button', 'route');
      b.type = 'button';
      const name = p.title || '(untitled)';
      b.append(el('span', 'route-label', name), el('span', 'route-hint', p.kind));
      // Distinct spoken names (§4) that still lead with the visible words (SC 2.5.3).
      b.setAttribute('aria-label', `${name} — put it in this ${p.kind}`);
      b.addEventListener('click', () => {
        fileInto(c => fileUnderEvents(c, nodeId, p.id, clocksOf(session.state().nodes.get(nodeId))), name, p);
      });
      rows.push(b);
    }

    ACTIONS.replaceChildren(...rows);
  };

  /**
   * WHERE THIS CAN BE DONE — the sub-screen behind the context route (3.13.0).
   *
   * `renderPlaces`' shape on the other axis, deliberately: two screens with one
   * shape are one thing to learn, which is the same reasoning `roles.ts` gives
   * for contexts and roles sharing machinery.
   *
   * The difference is what happens after. A container filing ends the card; this
   * returns to it. A context is a fact about the work, not a decision about when
   * it comes back, and consuming the sorting decision to record one would make
   * the cheap thing expensive again in a different currency.
   */
  const renderContexts = (nodeId: string, text: string, kind: string, heat: Heat | null): void => {
    const st = session.state();
    const places = allContexts(st);
    const node = st.nodes.get(nodeId);
    const already = new Set((node ? contextsOf(st, node) : []).map(c => c.id));

    PROMPT.textContent = 'Where can this be done?';
    CARD.textContent = text;

    const back = el('button', 'route ghost');
    back.type = 'button';
    back.append(el('span', 'route-label', 'Back'), el('span', 'route-hint', 'keep deciding when instead'));
    back.addEventListener('click', () => renderClarify(nodeId, text, kind, heat));

    const attach = (
      make: Parameters<Session['commit']>[0], said: string,
    ): void => {
      clearUndo();
      void commit(make, said).then(() => {
        // BACK TO THE SAME CARD. Nothing was routed, so the queue has not moved
        // and the item is still the one in front of you — now carrying the fact.
        //
        // The commit's own repaint would land here anyway; planting proved that.
        // This stays because it names the DESTINATION rather than relying on the
        // queue happening to agree — it returns to the clarify view of THIS item
        // specifically, rather than to whatever a fresh `refresh()` decides,
        // which on an unheated item is the heat card and not the six routes.
        renderClarify(nodeId, text, kind, heat);
        restoreFocus();
      });
    };

    const rows: HTMLElement[] = [back];

    // Naming one that does not exist is first, for the reason it is first on the
    // container screen: it is the case an import runs into constantly, and a
    // picker with nothing in it teaches the reader the feature is broken.
    const form = el('div', 'place-new');
    const input = document.createElement('input');
    input.type = 'text';
    input.id = 'triage-context-new';
    input.placeholder = 'at home, out, on the phone';
    const label = el('label', 'visually-hidden', 'Name a place where this can be done');
    label.setAttribute('for', 'triage-context-new');
    const make = el('button', 'route');
    make.type = 'button';
    make.append(el('span', 'route-label', 'Add it'), el('span', 'route-hint', 'a new place, and say this can be done there'));
    make.addEventListener('click', () => {
      const name = input.value.trim();
      if (!name) { input.focus(); return; }
      // An existing name attaches rather than minting a second one with the same
      // words — the same match `detail.ts` makes, case-insensitively, because
      // "Home" and "home" are one place and two would split the axis in half.
      const existing = allContexts(session.state())
        .find(c => (c.title || '').toLowerCase() === name.toLowerCase());
      attach(c => attachContextEvents(c, nodeId, existing?.id ?? c.id(),
        existing ? {} : { createNamed: name }), `It can be done ${name}.`);
    });
    form.append(label, input, make);
    rows.push(form);

    for (const c of places) {
      if (already.has(c.id)) continue;
      const b = el('button', 'route');
      b.type = 'button';
      const name = c.title || '(untitled)';
      b.append(el('span', 'route-label', name), el('span', 'route-hint', 'it can be done here'));
      b.setAttribute('aria-label', `${name} — this can be done here`);
      b.addEventListener('click', () => {
        attach(x => attachContextEvents(x, nodeId, c.id), `It can be done ${name}.`);
      });
      rows.push(b);
    }

    ACTIONS.replaceChildren(...rows);
  };

  /**
   * AS WHAT KIND OF WISH (Phase 0 of the structural assessment; read 7).
   *
   * The Someday route wrote `category: 'read'` for every single item, so a
   * store's whole Menu rendered as one group called Read — a wish to go
   * somewhere, a thing to try and something to save for, all filed as reading.
   * `docs/nd-collisions.md` entry 26 measured it and named the remedy in terms:
   * let the category a person already sees be chosen **at write time** instead
   * of silently defaulting, as *"a two-tap choice at the same moment `heat.set`
   * already asks one (ADR-0029's pattern), not a new surface, not a rank, not a
   * requirement"*. This is that, and nothing wider: the same card, one more
   * step, and a way past that says where it goes if you take it.
   *
   * ENTRY 27 IS WHY THERE IS A WAY PAST AT ALL. Two pre-registered experiments
   * found that effortful recording is done less AND that the accuracy benefit
   * is attenuated when it is done anyway — the price of recording is paid in
   * the thing the tool exists to provide. So this may never become a toll gate
   * in front of sorting; skipping it is one tap and it names its own outcome.
   *
   * REFERENCE DOES NOT COME HERE, and that is not an oversight. Reference
   * material genuinely is for reading, so `read` is the honest category for
   * that route rather than an invented one. The defect was only ever the
   * Someday default.
   *
   * The six answers come from `MENU_CATEGORIES` and `MENU_WORDS` — the one
   * source, in the order the Menu itself shows them, so this cannot become a
   * fourth hand-written copy of a list the app already holds. Entry 26's "not a
   * rank" is honored by taking that order unchanged rather than inventing one.
   */
  const renderMenuKinds = (
    nodeId: string, text: string, kind: string, heat: Heat | null,
    take: (category: MenuCategory) => void,
  ): void => {
    PROMPT.textContent = 'What kind of wish is it?';
    PROMPT.dataset.step = 'menu-kind';
    CARD.textContent = text;

    const back = el('button', 'route ghost');
    back.type = 'button';
    back.append(el('span', 'route-label', 'Back'),
      el('span', 'route-hint', 'keep deciding where it goes instead'));
    back.addEventListener('click', () => renderClarify(nodeId, text, kind, heat));

    const rows: HTMLElement[] = [back];
    for (const category of MENU_CATEGORIES) {
      const b = el('button', 'route');
      b.type = 'button';
      b.dataset.menuKind = category;
      b.append(el('span', 'route-label', MENU_WORDS[category]),
        el('span', 'route-hint', 'onto the Menu under this'));
      b.setAttribute('aria-label', `${MENU_WORDS[category]} — onto the Menu under this`);
      b.addEventListener('click', () => take(category));
      rows.push(b);
    }

    // THE WAY PAST NAMES WHERE IT GOES. "Skip" alone would leave the reader
    // guessing, and what it does is the thing this step exists to stop being
    // silent — so it says it, and says the page can change it.
    const past = el('button', 'route ghost');
    past.type = 'button';
    past.dataset.menuKind = 'skip';
    past.append(el('span', 'route-label', 'Do not say'),
      el('span', 'route-hint', `it goes under ${MENU_WORDS.read} — its own page can change that`));
    past.addEventListener('click', () => take('read'));
    rows.push(past);

    ACTIONS.replaceChildren(...rows);
  };

  const renderClarify = (nodeId: string, text: string, kind: string, heat: Heat | null): void => {
    // THE WALKTHROUGH'S OWN SENTENCE (3.9.1). This read `Clarify (cold):` — the
    // internal name for the step, plus the stored heat value in brackets. Two
    // schema words on the second thing a new reader ever does, and the rule is
    // that the schema word never reaches a surface.
    //
    // "What is this?" is not a new phrase: screen 3 of the walkthrough already
    // promises the app "takes them one at a time and asks what each one is". The
    // prompt was contradicting the only sentence that had prepared anybody for
    // it.
    //
    // The heat echo goes with it. It was there to say which pass you are on, and
    // the prompt changing from "Hot or cold?" and the two buttons becoming eight
    // already say that louder than a bracket can. What you said is read back
    // where it does something — on the offer card, as "you said it was hot".
    PROMPT.textContent = 'What is this?';
    PROMPT.dataset.step = 'clarify';
    showing = nodeId;
    CARD.textContent = text;
    paintContext(nodeId);
    // ONE COMMIT PATH, TWO DOORS (3.26.0). The Someday route now asks what kind
    // of wish it is first, so the batch is written from two places — the button
    // directly for the other seven routes, and the kind step for that one. A
    // second copy of this would be a second set of words for the same act, and
    // the shed-date sentence below is exactly the kind of thing that drifts.
    const takeRoute = (route: ClarifyRoute, label: string, category: MenuCategory = 'read'): void => {
      {
        // Supersede any earlier undo before committing — undo only ever takes
        // back the most recent route.
        clearUndo();
        // Snapshotted BEFORE the commit: the route is about to shed these, and
        // afterwards there is nothing left to read them off.
        const shed = restorableClocksOf(session.state().nodes.get(nodeId));
        // AND SAID, rather than done quietly. someday/reference land on the
        // Menu, which by law 6 can hold no demand — so the dates genuinely
        // cannot come with it. That is not a reason to let a date disappear
        // without a word; it is the reason the word is owed.
        const sheds = (route === 'someday' || route === 'reference') ? shed.length : 0;
        const said = sheds === 0 ? `Routed to ${label}.`
          : sheds === 1 ? `Routed to ${label}. Its date comes off — nothing on your wishes makes a demand. Undo puts it back.`
            : `Routed to ${label}. Its ${sheds} dates come off — nothing on your wishes makes a demand. Undo puts them back.`;
        void commit(ctx => routeEvents(ctx, nodeId, route, kind as never,
          demandClocksOf(session.state().nodes.get(nodeId)), category), said)
          .then(ok => {
            // Offer to take it back, whichever route it was — the answer to
            // "where did it go and how do I undo it". Captured with the id, route
            // and prior kind so it reverses this card after the queue advances.
            if (ok) showUndo(nodeId, route, kind as NodeKind, label, undefined, undefined, shed);
            // Start the timer only if the route actually landed, and only after
            // the card has advanced — the timer's own region is untouched by that.
            // OFFER, do not start. The route is the decision; the timer is a tool.
            if (ok && route === 'do-now') offerDoNow(nodeId);
            restoreFocus();
          });
      }
    };

    const routeButtons = ROUTES.map(({ route, label, hint }) => {
      const b = el('button', 'route');
      b.type = 'button';
      // `data-route` on every one of them, because the walks have twice been
      // timed out by keying on a label or a position (LESSONS 180) — and the
      // Someday button is about to become the one with a step behind it.
      b.dataset.route = route;
      b.append(el('span', 'route-label', label), el('span', 'route-hint', hint));
      b.addEventListener('click', () => {
        if (route === 'someday') {
          renderMenuKinds(nodeId, text, kind, heat, cat => takeRoute(route, label, cat));
          return;
        }
        takeRoute(route, label);
      });
      return b;
    });
    ACTIONS.replaceChildren(...routeButtons);

    // WHERE, offered beside the six WHENs. Last in the row because the common
    // case is still "when", and first-class rather than buried because for an
    // imported backlog it is the only question that matters.
    //
    // AND IT SAID "PLACE" FOR A THING THAT IS NOT ONE (3.11.1). It files into a
    // CONTAINER — `renderPlaces` filters `CONTAINER_KINDS`, so a project, an
    // outcome, an area or a goal — and it called that "a place". But *place* is
    // already taken, by the app, for something else entirely: `kind-words.ts`
    // has `context: 'A place'` with the comment "where work can be DONE, which
    // is not a thing to do", and two live controls on the same page say `Narrow
    // the places` to a screen reader about exactly those.
    //
    // So the two questions somebody asked for — *where can this be done* and
    // *what is this part of* — were one word on this card, and the flowcharts
    // page hedged between them in writing: "file it under a place or a project".
    // It takes the detail sheet's own words now, which is the vocabulary the
    // rest of the app already teaches.
    //
    // `data-route` so the walks stop keying on the label. Both of them waited
    // for the literal "Put it somewhere", which is the gate-pins-the-defect
    // shape this repo has already paid for twice (LESSONS 180).
    const put = el('button', 'route');
    put.type = 'button';
    put.dataset.route = 'put-under';
    put.append(el('span', 'route-label', 'Put it under something'),
      el('span', 'route-hint', 'a project, area or goal — make one if it is not there'));
    put.addEventListener('click', () => renderPlaces(nodeId, text, kind, heat));
    ACTIONS.append(put);

    // WHERE IT CAN BE DONE, on the card (3.13.0, entry 27).
    //
    // The evidence is the reason this is here rather than three taps into a
    // detail sheet. Two pre-registered experiments found reminder-setting falls
    // when it costs more effort at every memory load, AND that the accuracy
    // benefit it would have delivered is attenuated when it is done anyway —
    // so the price of recording is not paid in convenience, it is paid in the
    // thing the tool exists to provide (`docs/nd-collisions.md` entry 27).
    //
    // The place axis has been readable since 2.2.0 and a real store carries
    // almost none of it, because the only way to say it was to open an item,
    // expand its sheet and scroll. A filter over an axis nobody can afford to
    // record reads as a broken feature rather than an empty one (entry 24).
    //
    // IT ANNOTATES, IT DOES NOT RESOLVE, and that is the difference from the
    // route above it. Filing into a container is a decision about where a thing
    // lives and it ends the card; saying where a thing can be DONE is a label,
    // and the item still has to be sorted. So this returns to the six routes
    // with the same item in front of you rather than advancing the queue.
    const where = el('button', 'route');
    where.type = 'button';
    where.dataset.route = 'add-context';
    where.append(el('span', 'route-label', 'Say where it can be done'),
      el('span', 'route-hint', 'at home, out, on the phone — it stays in front of you'));
    where.addEventListener('click', () => renderContexts(nodeId, text, kind, heat));
    ACTIONS.append(where);
    // And the way past, last of all: every answer first, then the way out for
    // when none of them is available yet.
    ACTIONS.append(skipControl(nodeId));
  };

  openBtn?.addEventListener('click', () => { refresh('ask'); });

  function refresh(after?: 'capture' | 'ask'): void {
    // A capture closes the door for this visit; asking for the inbox opens it.
    if (after === 'capture') suppressed = true;
    if (after === 'ask') suppressed = false;
    const st = session.state();
    // Two scans, not four: the heads and the gauge all derive from these.
    const inbox = unclarified(st);
    const heatQueue = needsHeat(st);
    // NO COUNT (1.42.1 — the comments here and in the smoke walk said 1.43.0 in
    // seven places until 3.10.0, and there has never been a 1.43.x; the change
    // is CHANGELOG.md 1.42.1, the same day as ADR-0085). The reader is told what
    // is TRUE of these things, not
    // how many of them there are.
    //
    // `12 to clarify` is the countable batch V2 stage 1 deleted from the
    // coverage gauge, reintroduced on the surface best placed to do damage with
    // it: a number that only goes up as you put things down, sitting on the
    // screen you arrive at. It converts a good day's capture into a visible
    // debt, which is the mechanism `docs/planning-for-humans.md` says stops the
    // dumping — and the dumping is the one path this app cannot afford friction
    // on. The capture confirmation already refuses a count for this reason, and
    // so does the dump commit; this was the last place still keeping score.
    //
    // What replaces it is the fact the count was obscuring. These items are
    // covered — the gate clocked each one as it was written — so sorting is not
    // what rescues them. It decides WHERE they come back. Saying so makes the
    // step optional in words as well as in fact (ADR-0029, ADR-0085).
    //
    // AND WHERE YOU ARE IN THE PILE, BY LANDMARK RATHER THAN BY TALLY (3.9.3).
    //
    // Walked as a reader: inside this surface there is no way to tell whether
    // this is the second card of thirty or the last one. The count is refused
    // above and stays refused — but the thing that was actually missing is not
    // a number, it is the two BOUNDARIES, and the app already computes both and
    // says neither.
    //
    // `fresh()` below prefers the first card not passed over this sitting, and
    // when every one HAS been passed it falls back to `q[0]` and starts the pile
    // again. Silently. So somebody can go round a sorting session for ever,
    // re-declining the same cards, with nothing telling them they have come
    // round — which is the disorientation, and it is not a counting problem.
    //
    // Two landmarks, no digits (the smoke walk asserts the visible text carries
    // none): the last card you have not been past, and having been round all of
    // them once. Both are facts about THIS SITTING and both are computed from
    // `passed`, which is in memory and is never written — so nothing durable is
    // created, exactly as the note above requires. Neither is a fraction: a
    // boundary is not progress toward an end, which is why `src/timer.ts` can
    // refuse the one and this can say the other.
    //
    // The round-once line is where "you can stop" belongs. ADR-0029 and
    // ADR-0085 make sorting optional and the standing sentence says so in
    // general; the moment somebody is about to re-decline a card they already
    // declined is the moment it is worth saying in particular.
    const unpassed = inbox.filter(n => !passed.has(n.id)).length;
    const roundOnce = inbox.length > 0 && unpassed === 0;
    const lastFresh = inbox.length > 1 && passed.size > 0 && unpassed === 1;
    GAUGE.textContent = inbox.length === 0
      ? 'Nothing here is waiting to be sorted.'
      : roundOnce
        ? 'You have been round all of these once. They are held either way, so this is a fine place to stop.'
        : lastFresh
          ? 'This is the last one you have not been past. They are held either way.'
          : 'These are held either way. Sorting decides where they come back, not whether.';
    // AND WHAT THE HUB'S DOOR SAYS (3.9.1) — see the note on `#triage-count`.
    // The same fact the gauge carries, in the shape a door needs, and with no
    // number in it for the reason the line above has none.
    const doorCount = document.querySelector<HTMLElement>('#triage-count');
    if (doorCount) {
      doorCount.textContent = inbox.length === 0 ? '' : 'What each one is, and when it comes back.';
    }
    // The count still exists for the WALKS, which need to watch a queue drain
    // and cannot read a sentence that deliberately does not change. A data
    // attribute is not a reader surface: `tools/a11y.mjs` measures what is
    // rendered, and the smoke walk asserts the visible text carries no digit at
    // all. Keeping the number here rather than in a hidden element is on
    // purpose — a visually-hidden count is still a count to a screen-reader
    // user, and they are owed the same freedom from the tally as anybody else.
    GAUGE.dataset.waiting = String(inbox.length);
    GAUGE.dataset.unheated = String(heatQueue.length);
    // AND THE OTHER NUMBER, for the same reason (3.10.0). `waiting` is the whole
    // queue the surface will hand you, arrivals included; `yours` is what
    // `#triage-here` claims. Two different facts, and a walk that reads one of
    // them to check the other is the shape hub LESSONS 153 is about — a check
    // whose sentence and predicate are different things. Both seams, neither a
    // reader surface.
    //
    // ONE EXPRESSION FEEDS BOTH THE SEAM AND THE WORDS, and it has to. The first
    // version computed `inbox.filter(n => !n.arrived).length` here and again
    // where the line is painted — so a plant that changed the painted number
    // left the seam correct and the whole walk stayed green. A test hook derived
    // separately from the thing it vouches for does not vouch for it.
    const yours = inbox.filter(n => !n.arrived).length;
    GAUGE.dataset.yours = String(yours);

    // Heat pass first while there is anything unheated; then clarify. Both are
    // one card; the surface hides itself when the inbox is clear. A running
    // do-now timer is deliberately NOT cleared here — it lives in its own region.
    //
    // WHAT WAS PASSED OVER GOES TO THE BACK, NOT AWAY (1.25.0). Prefer the
    // first card not passed this session; when every one has been, start again
    // from the top rather than showing an empty surface. That is `work.ts`'s
    // rule for its own declined set, and it matters more here: an inbox that
    // emptied itself because somebody skipped everything would be the app
    // hiding work, which is the opposite of law 1 and the exact fear that makes
    // a person keep forty tabs open.
    //
    // The GAUGE above is untouched by any of this. It counts what is in the
    // inbox, which is what the number means and what somebody would check it
    // against — a count that shrank as things were passed over would be the
    // surface keeping score of what was avoided.
    const fresh = <T extends { id: string }>(q: readonly T[]): T | null =>
      q.find(n => !passed.has(n.id)) ?? q[0] ?? null;
    // An item the reader asked to sort outright is not offered a heat card.
    // ASKED FOR OUTRIGHT, so it jumps the heat queue entirely (V2 stage 3).
    //
    // The first version only FILTERED this item out of the heat queue, which is
    // not the same thing at all: the surface simply fell through to somebody
    // else's heat card, the prompt still said "Hot or cold?", and the item the
    // reader had just asked to sort was nowhere. The walk caught it saying
    // exactly that. "Choose where it goes" is a request about THIS item, so it
    // has to put THIS item in front of you.
    // THE SWEEP IS FOR A PILE, and a pile is what it was designed against
    // (1.39.3). ADR-0029 calls heat "an optional, lighter-weight first pass"
    // whose whole value is that a cheap hot/cold run across many items is easier
    // than a run of six-route decisions — and that skipping it "costs nothing
    // but a little of clarify's context".
    //
    // But it LED, always. For one captured thought that meant two screens and
    // twelve choices to put one thing away: a four-choice question that routes
    // nothing, then the eight that actually decide. An optional step you have to
    // decline on every single item is not optional, it is a toll with a bypass.
    //
    // So it leads when there is a pile to sweep, and gets out of the way when
    // there is not. Nothing is removed: below the threshold the heat question is
    // simply not the first thing, and every item can still be marked hot or cold
    // from its own sheet.
    const SWEEP_WORTH_IT = 4;
    if (heatQueue.length === 0) sweepUnderWay = false;
    else if (heatQueue.length >= SWEEP_WORTH_IT) sweepUnderWay = true;
    const sweeping = sweepUnderWay;
    const askedFor = inbox.find(n => straightToSort.has(n.id) && !passed.has(n.id));
    const heatItem = (askedFor || !sweeping)
      ? null
      : fresh(heatQueue.filter(n => !straightToSort.has(n.id)));
    const clarifyItem = askedFor ?? fresh(inbox);

    // DECIDING WHILE DUMPING STOPS THE DUMPING (1.39.2).
    //
    // This surface used to reveal itself the moment an unclarified capture
    // existed — so putting one thing down was answered by "Hot or cold?" about
    // the thing you had just put down, before you had finished thinking. Ten
    // things in a row meant ten interruptions, on the one path the whole app is
    // built to keep frictionless.
    //
    // `docs/planning-for-humans.md` says it in terms, and the many-line capture
    // work was built on it: nothing is sorted, split or filed on the way in. The
    // main screen was the exception nobody had looked at.
    //
    // So a capture may CHANGE what is shown here; it may not TURN IT ON. It
    // appears when you arrive with things waiting, and when you ask for it —
    // never as an answer to your own typing.
    // Suppressed, but something IS waiting: show the door rather than nothing.
    // Hiding the surface outright would strand the inbox for the rest of the
    // visit, which is a worse failure than the interruption being removed.
    const waiting = Boolean(heatItem ?? clarifyItem);
    if (openBtn) openBtn.hidden = !(suppressed && waiting);
    if (suppressed && waiting) {
      REGION.hidden = false;
      CARD.hidden = true;
      CARD.textContent = '';
      // HIDDEN, not emptied. `#triage-prompt` is a heading, and a heading with no
      // text is an axe `empty-heading` violation — caught by the a11y walk the
      // moment this state was given one of its own to be measured in.
      PROMPT.hidden = true;
      PROMPT.textContent = '';
      ACTIONS.replaceChildren();
      // THE DOOR STATE IS THE ARRIVAL SCREEN, so the count is not on it. This is
      // the exact state ADR-0085 cleared: you have arrived, things are waiting,
      // and the app is not asking you anything. A number here would be the
      // visible debt that ADR removed, wearing different words.
      setHere(null);
      paintContext(null);
      showing = null;
      return;
    }
    CARD.hidden = false;
    PROMPT.hidden = false;
    // AND HERE IT IS ON, because there is a card in front of you and you walked
    // in to get it. Same condition as `CARD.hidden`, one line apart, so the two
    // cannot drift into the count appearing on a screen with no card on it.
    //
    // IT COUNTS WHAT YOU PUT DOWN, NOT WHAT YOU BROUGHT IN — `!n.arrived`, which
    // is `inboxGauge`'s rule and not a second one. 2.38.0 settled this for the
    // headline in terms that bind any number on this surface: "a number in the
    // app's chrome saying you are 1,171 behind, because you once brought a file
    // in, is the opposite of [law 8] — and it is not even true: an arrival is
    // not something you owe today."
    //
    // Found by the assertion below going red. The import fixture brings in three
    // rows and the queue moved by exactly three, because `unclarified` holds
    // arrivals deliberately — the clarify surface is where you GO to sort, and
    // saying the inbox is clear while a thousand imported rows sit there would
    // be the dishonest half of that trade. So the SURFACE offers them and the
    // COUNT does not claim them, which is the same split `inboxGauge` already
    // makes one file over.
    //
    // STILL NOT RIGHT, and named rather than hidden: with an import in the store
    // the room will hand you cards the count did not promise. The fix is not a
    // bigger number, it is arrivals having their own set to be worked through —
    // which is the next piece of work and is why this is the forward-compatible
    // half of the trade rather than something that has to be undone.
    // AND IT COUNTS THE PASS YOU ARE IN, not the queue behind both of them.
    //
    // `yours` counts things with no route yet, and the hot-or-cold pass sets
    // `heat` and never touches `route` — so by construction the number could not
    // move until the SECOND pass began. Five answers, five times the same
    // number, on the screen whose whole job is to feel finishable, and then it
    // counted down properly through "What is this?" as though the first pass had
    // not happened.
    //
    // The per-pass count already existed: `needsHeat` applies `!n.arrived` just
    // as `yours` does, so the two are directly comparable, and it is already
    // published as `dataset.unheated` for the walk to read. This says whichever
    // one the reader is actually looking at, decided by the same `heatItem` the
    // render picked above rather than by a second guess at which pass is up.
    // AND THE SENTENCE SAYS WHICH QUESTION IT IS COUNTING (3.23.13).
    //
    // 3.23.6 made this count the pass in front of you rather than the queue
    // behind both, which was necessary and not sufficient: both passes then
    // printed the SAME SENTENCE. A cold reader watched it read 7, 6, 5, 4, 3, 2
    // and then 9 on the next tap — the heat pass had drained and the sorting
    // pass had started — and the only thing telling them a second question had
    // begun was a number going the wrong way under unchanged words. A count
    // that changes meaning silently is worse than no count.
    const hereNow = heatItem
      ? heatQueue.filter(n => !straightToSort.has(n.id)).length
      : yours;
    const num = hereNow === 1 ? 'One' : String(hereNow);
    setHere(heatItem
      ? `${num} to say hot or cold about.`
      : `${num} here to work through.`);

    const mayReveal = !suppressed;
    if (heatItem) {
      if (mayReveal) REGION.hidden = false;
      renderHeat(heatItem.id, heatItem.title);
    } else if (clarifyItem) {
      if (mayReveal) REGION.hidden = false;
      renderClarify(clarifyItem.id, clarifyItem.title, clarifyItem.kind, clarifyItem.heat);
    } else {
      REGION.hidden = true;
      showing = null;
      CARD.textContent = '';
      setHere(null);
      paintContext(null);
      ACTIONS.replaceChildren();
    }
  }

  /**
   * Re-label an offer that is already on screen (1.10.0).
   *
   * The timer length is set in the (i) panel, which can be open while an offer
   * is showing — and a button that says "two minutes" and starts twenty is the
   * class of lie 1.7.2 was spent correcting. This is DELIBERATELY not part of
   * `refresh`: rebuilding the clarify card on every commit anywhere in the app
   * changes which card is on screen mid-interaction, which the smoke walk
   * caught immediately. Touching one button's words does not.
   */
  function relabelTimer(): void {
    const startBtn = DONOW.querySelector<HTMLButtonElement>('[data-start-timer]');
    if (startBtn) startBtn.textContent = `Start ${timerWordsLower(timerMinutesOf(session.state()))}`;
  }

  refresh();
  return { refresh, relabelTimer };
}
